import React, { useState } from "react";
import { Link } from "react-router-dom";
import { currency } from "@/lib/format";
import { ArrowRight, Beef, Gift, Check } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { useReveal } from "@/hooks/useReveal";
import Img from "@/components/Img";
import {
  DEFAULT_FREQUENCY,
  WAGYU_BURGER_VALUE,
  frequenciesForBox,
  annualBurgerValue,
  annualDiscountValue,
} from "@/config/subscriptionConfig";

/** Per-serve price: price ÷ (box weight in grams ÷ 250g), rounded to
 * nearest 10 cents. Used on both /boxes cards and box detail pages so
 * shoppers can compare a $99 box to a $329 box on equal footing. */
export function perServePrice(price, weightStr) {
  const kg = parseFloat(String(weightStr || "").replace(/[^\d.]/g, ""));
  if (!kg || !price) return null;
  const grams = kg * 1000;
  const servings = grams / 250;
  const perServe = price / servings;
  return Math.round(perServe * 10) / 10;
}

/**
 * BoxCard — Feb 2026 subscription-conversion redesign.
 *
 * Subscribe is the DEFAULT choice. One-time exists but as a muted text
 * link below the primary CTA so choosing it takes a deliberate action.
 * The subscribe value hierarchy is BURGERS-FIRST: $18 free Wagyu pack
 * is the headline (bigger, bolder), the 5% saving is supporting text.
 * See `/app/frontend/src/config/subscriptionConfig.js` for the shared
 * constants (burger value, weekly-eligible boxes, annualised copy).
 */
export default function BoxCard({ box, index = 0, priority = false, hideFrequency = false }) {
  const { addItem, setSubscription, setFrequency } = useCart();
  const savings = (box.price - box.subscription_price).toFixed(2);
  const savingsPct = box.subscription_discount_pct || 5;
  const isFounding = !!box.founding_active && box.standard_price && box.standard_price > box.price;
  const isHero = box.slug === "family-feast-box";
  const perServe = perServePrice(box.price, box.weight);

  const freqOpts = frequenciesForBox(box.slug);
  const [freq, setFreq] = useState(DEFAULT_FREQUENCY);
  const [addedKind, setAddedKind] = useState(null);
  const { ref: cardRef, visible } = useReveal();

  const annualBurgers = annualBurgerValue(freq);
  const annualSavings = annualDiscountValue(box, freq);

  const addOnce = () => {
    if (addedKind) return;
    setAddedKind("once");
    setSubscription(false);
    const res = addItem(
      { slug: box.slug, kind: "box", quantity: 1, name: box.name, price: box.price, image: box.image },
      { triggerUpsell: true },
    );
    if (!res?.duplicate) toast.success(`${box.name} added to cart`);
    setTimeout(() => setAddedKind(null), 900);
  };

  const addSub = () => {
    if (addedKind) return;
    setAddedKind("sub");
    setSubscription(true);
    setFrequency(freq);
    const res = addItem({ slug: box.slug, kind: "box", quantity: 1, name: box.name, price: box.price, image: box.image });
    const label = freqOpts.find((f) => f.key === freq)?.long || "Subscribe";
    if (!res?.duplicate) toast.success(`${box.name} · ${label} · $${WAGYU_BURGER_VALUE} of free burgers included`);
    setTimeout(() => setAddedKind(null), 900);
  };

  return (
    <div
      ref={cardRef}
      className={`tbb-card hover-lift flex flex-col h-full group ${visible ? "tbb-rise-in" : ""} ${isHero ? "ring-2 ring-[color:var(--tbb-burgundy)] shadow-lg md:scale-[1.02]" : ""}`}
      style={visible ? { animationDelay: `${(index % 4) * 90}ms` } : { opacity: 0 }}
      data-testid={`box-card-${box.slug}`}
    >
      <Link to={`/boxes/${box.slug}`} className="relative block overflow-hidden bg-[color:var(--tbb-bg-soft)] aspect-[4/5]" data-testid={`box-image-link-${box.slug}`}>
        <Img
          src={box.image}
          alt={box.name}
          width={800}
          height={1000}
          priority={priority}
          className="img-cover transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-0"
          onError={(e) => {
            const fb = "https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=1200&q=80";
            if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
          }}
        />
        <Img
          src={`/boxes/${box.slug}-packed.jpg`}
          alt={`${box.name} packed and ready to ship`}
          aria-hidden="true"
          width={800}
          height={1000}
          className="absolute inset-0 img-cover opacity-0 transition-all duration-700 ease-out group-hover:opacity-100 group-hover:scale-105"
          data-testid={`box-image-packed-${box.slug}`}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        {box.badge && (
          <span className="absolute top-4 left-4 badge-pill badge-burgundy" data-testid={`box-badge-${box.slug}`}>
            {box.badge}
          </span>
        )}
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white text-xs uppercase tracking-widest font-bold">
          <span className="bg-black/60 backdrop-blur px-3 py-1">{box.weight}</span>
        </div>
      </Link>

      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="overline text-[color:var(--tbb-burgundy)]">{box.ideal_for}</div>
            <Link
              to={`/boxes/${box.slug}`}
              className="hover:text-[color:var(--tbb-burgundy)] transition-colors"
              data-testid={`box-name-link-${box.slug}`}
            >
              <h3 className="font-display text-2xl mt-1" data-testid={`box-name-${box.slug}`}>{box.name}</h3>
            </Link>
          </div>
        </div>
        <Link
          to={`/boxes/${box.slug}`}
          className="text-sm text-[color:var(--tbb-muted)] mb-4 hover:text-[color:var(--tbb-ink)] transition-colors"
          data-testid={`box-desc-link-${box.slug}`}
        >
          {box.description}
        </Link>

        <ul className="text-xs text-[color:var(--tbb-muted)] space-y-1 mb-6 flex-1" data-testid={`box-contents-${box.slug}`}>
          {box.contents.map((c) => (
            <li key={c} className="flex items-start gap-2">
              <Beef size={12} className="mt-0.5 text-[color:var(--tbb-burgundy)] shrink-0" />
              <span>{c}</span>
            </li>
          ))}
        </ul>

        {/* =====================================================================
            SUBSCRIBE PANEL — dominant. Always the default. Feb 2026 redesign.
            ===================================================================== */}
        <div
          className="relative border-2 border-[color:var(--tbb-burgundy)] bg-[color:var(--tbb-burgundy)]/6 p-4 mb-3"
          data-testid={`box-subscribe-panel-${box.slug}`}
        >
          <span className="best-value-tag">Best Value</span>

          {/* Burgers-first hierarchy — headline is the ACTUAL biggest gift
              ($18 pack), 5% is supporting. Reversed from the pre-Feb 2026 UI
              which led with the 5% and buried the burgers. */}
          <div className="flex items-start gap-2 mb-2">
            <Gift size={18} className="text-[color:var(--tbb-burgundy)] shrink-0 mt-0.5" />
            <div className="leading-tight">
              <div className="font-black uppercase tracking-widest text-[color:var(--tbb-burgundy)] text-[14px]">
                FREE Wagyu Burgers — ${WAGYU_BURGER_VALUE} value, every delivery
              </div>
              <div className="text-[11px] text-[color:var(--tbb-muted)] mt-0.5">
                Plus save {currency(savings)} ({savingsPct}%) on every order
              </div>
            </div>
          </div>

          {/* Price row — sub price is dominant, one-time appears as tiny
              strike-through reference below (no separate one-time card). */}
          <div className="flex items-baseline gap-3 mb-3">
            <div className="font-display text-3xl leading-none text-[color:var(--tbb-burgundy)]" data-testid={`box-sub-price-${box.slug}`}>
              {currency(box.subscription_price)}
            </div>
            {isFounding ? (
              <div className="text-[12px] text-[color:var(--tbb-muted)]">
                <span className="line-through" data-testid={`box-standard-price-${box.slug}`}>
                  {currency(box.standard_price)}
                </span>{" "}
                <span className="uppercase tracking-widest text-[10px] font-bold text-[color:var(--tbb-burgundy)]">founding</span>
              </div>
            ) : (
              <div className="text-[12px] text-[color:var(--tbb-muted)] line-through">
                {currency(box.price)} one-time
              </div>
            )}
          </div>

          {/* Frequency segmented buttons — visible options, no dropdown.
              Weekly is conditional on box slug (see subscriptionConfig).
              Sept 2026 (Part 2.8): hidden on the Home page via
              `hideFrequency` prop so shoppers pick the BOX first without
              getting stuck on delivery cadence upfront. Full frequency
              picker moves to the product/box detail page and cart. */}
          {!hideFrequency && (
          <div className="mb-3" data-testid={`box-frequency-${box.slug}`}>
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--tbb-muted)] font-bold">
                Delivery frequency
              </div>
              {(() => {
                const kg = parseFloat(String(box.weight).replace(/[^\d.]/g, "")) || 0;
                const chosen = freqOpts.find((f) => f.key === freq);
                const days = chosen?.days || 14;
                const perWeek = (kg / days) * 7;
                if (!kg) return null;
                return (
                  <div
                    className="text-[10px] uppercase tracking-widest text-[color:var(--tbb-burgundy)] font-bold"
                    data-testid={`box-per-week-${box.slug}`}
                  >
                    ≈{perWeek.toFixed(perWeek < 10 ? 2 : 1).replace(/\.?0+$/, "")}kg/wk
                  </div>
                );
              })()}
            </div>
            <div className={`grid gap-1.5 ${freqOpts.length === 3 ? "grid-cols-3" : "grid-cols-2"}`}>
              {freqOpts.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFreq(f.key)}
                  aria-pressed={freq === f.key}
                  className={`relative px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest border transition ${
                    freq === f.key
                      ? "bg-[color:var(--tbb-burgundy)] text-white border-[color:var(--tbb-burgundy)]"
                      : "bg-white text-[color:var(--tbb-ink)] border-[color:var(--tbb-line)] hover:border-[color:var(--tbb-burgundy)]"
                  }`}
                  data-testid={`box-freq-${f.key}-${box.slug}`}
                >
                  {f.short}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Annualised value — this is the sentence that converts.
              At fortnightly: 26 × $18 = $468/yr of free burgers. Way
              more persuasive than "5% off". */}
          <div
            className="text-[11px] text-[color:var(--tbb-ink)] mb-3 leading-tight"
            data-testid={`box-annual-value-${box.slug}`}
          >
            Over a year that's{" "}
            <span className="font-black text-[color:var(--tbb-burgundy)]">${annualBurgers} in free burgers</span>
            {annualSavings > 0 && (
              <>
                {" "}plus <span className="font-black">${annualSavings} saved</span>
              </>
            )}
            .
          </div>

          <button
            onClick={addSub}
            disabled={!!addedKind}
            aria-live="polite"
            className={`btn-burgundy w-full text-sm py-3 transition-all ${
              addedKind === "sub" ? "!bg-emerald-700 pointer-events-none" : ""
            }`}
            data-testid={`subscribe-${box.slug}`}
          >
            {addedKind === "sub" ? (
              <span className="inline-flex items-center gap-1"><Check size={14} strokeWidth={3} /> Subscribed</span>
            ) : (
              <>Subscribe & Save <ArrowRight size={14} /></>
            )}
          </button>
          <div className="text-[10px] text-center text-[color:var(--tbb-muted)] mt-1.5 uppercase tracking-widest">
            Skip, pause or cancel anytime
          </div>
        </div>

        {/* Family Feast cost anchor — kept from prior design; strong social proof. */}
        {box.slug === "family-feast-box" && (
          <div
            className="mb-3 border border-[color:var(--tbb-line)] bg-white p-3 text-[11px] leading-tight text-[color:var(--tbb-ink)]"
            data-testid="box-cost-anchor-family-feast"
          >
            <div className="font-black uppercase tracking-widest text-[10px] text-[color:var(--tbb-burgundy)] mb-1">
              What families actually spend
            </div>
            <div>
              Perth families report <b>$250+ per week</b> on supermarket protein.
              This is <b>less</b>, restaurant-quality, and lands cold to the door.
            </div>
          </div>
        )}

        {isFounding && (
          <div
            className="mb-3 px-3 py-2 bg-[color:var(--tbb-burgundy)] text-white text-[10px] font-bold uppercase tracking-[0.14em] text-center leading-snug"
            data-testid={`box-founding-badge-${box.slug}`}
          >
            Founding Price — Locked In For Early Subscribers
          </div>
        )}

        {perServe && (
          <div className="text-[10.5px] text-[color:var(--tbb-muted)] mb-3 text-center" data-testid={`box-per-serve-${box.slug}`}>
            ≈ ${perServe.toFixed(2)} per 250g serve
          </div>
        )}

        {/* One-time — secondary button. Deliberately less visual weight
            than the Subscribe & Save CTA (no fill, thin border, muted
            copy) so opting out of the subscription flow still takes a
            conscious act. Matches the Phase 1 spec: "One-time remains
            available but is the secondary choice, not the default state." */}
        <button
          onClick={addOnce}
          disabled={!!addedKind}
          aria-live="polite"
          className={`w-full py-2.5 text-[11px] font-bold uppercase tracking-widest border transition ${
            addedKind === "once"
              ? "!bg-emerald-50 !border-emerald-700 !text-emerald-700"
              : "border-[color:var(--tbb-line)] text-[color:var(--tbb-muted)] hover:border-[color:var(--tbb-ink)] hover:text-[color:var(--tbb-ink)] hover:bg-[color:var(--tbb-bg-soft)]"
          }`}
          data-testid={`buy-once-${box.slug}`}
        >
          {addedKind === "once" ? (
            <span className="inline-flex items-center gap-1"><Check size={12} strokeWidth={3} /> Added</span>
          ) : (
            <>Just once · {currency(box.price)}</>
          )}
        </button>
      </div>
    </div>
  );
}
